import red from '@material-ui/core/colors/red';
import createBreakpoints from '@material-ui/core/styles/createBreakpoints';
import createMuiTheme, { ThemeOptions } from '@material-ui/core/styles/createMuiTheme';

function pxToRem(value) {
  return `${value / 16}rem`;
}

export function remToPx(value) {
  return `${value * 16}px`;
}

const breakpoints = createBreakpoints({
  values: {
    xs: 0,
    sm: 640,
    md: 1000,
    lg: 1340,
    xl: 1800,
  },
});

export const getTheme = (type: 'light' | 'dark' | undefined = 'light') => {
  const theme: ThemeOptions = {
    spacing: 8,

    palette: {
      type,
      divider: '#cecece',
      text: {
        secondary: 'rgba(0, 0, 0, 0.6)',
      },
      primary: {
        light: '#4F94F7',
        main: '#05149E',
        dark: '#171460',
        contrastText: '#f1f1f1',
      },
      secondary: {
        light: '#ff738c',
        main: '#FF395F',
        dark: '#c50036',
        // contrastText: '#0f0f0f',
      },
      error: {
        light: red[400],
        main: red[600],
        dark: red[900],
      },
    },
    overrides: {
      MuiTypography: {
        h1: {
          fontSize: pxToRem(48),
          fontWeight: 'normal',
          fontStyle: 'normal',
          fontStretch: 'normal',
          lineHeight: 'normal',
          letterSpacing: '-0.6px',
          color: 'rgba(0, 0, 0, 0.87)',
          [breakpoints.down('sm')]: {
            fontSize: pxToRem(36),
            fontWeight: 500,
            letterSpacing: '-0.4px',
          },
        },
        h2: {
          fontSize: pxToRem(42),
          fontWeight: 'normal',
          fontStyle: 'normal',
          fontStretch: 'normal',
          lineHeight: 'normal',
          letterSpacing: '-0.4px',
          color: 'rgba(0, 0, 0, 0.87)',
          [breakpoints.down('sm')]: {
            fontSize: pxToRem(28),
            fontWeight: 500,
            letterSpacing: '-0.3px',
          },
        },
        h3: {
          fontSize: pxToRem(36),
          fontWeight: 'normal',
          fontStyle: 'normal',
          fontStretch: 'normal',
          lineHeight: 'normal',
          letterSpacing: 'normal',
          color: 'rgba(0, 0, 0, 0.87)',
          [breakpoints.down('sm')]: {
            fontSize: pxToRem(24),
            fontWeight: 500,
            letterSpacing: 'normal',
          },
        },
        h4: {
          fontSize: pxToRem(28),
          fontWeight: 'normal',
          fontStyle: 'normal',
          fontStretch: 'normal',
          lineHeight: 'normal',
          letterSpacing: '0.2px',
          color: 'rgba(0, 0, 0, 0.87)',
          [breakpoints.down('sm')]: {
            fontSize: pxToRem(21),
            fontWeight: 500,
            letterSpacing: '0.2px',
          },
        },
        h5: {
          fontSize: pxToRem(18),
          fontWeight: 500,
          fontStyle: 'normal',
          fontStretch: 'normal',
          lineHeight: '24px',
          letterSpacing: '-0.2px',
          color: 'rgba(0, 0, 0, 0.87)',
          [breakpoints.down('sm')]: {
            fontSize: pxToRem(16),
            fontWeight: 500,
            letterSpacing: '0.2px',
          },
        },
        h6: {
          letterSpacing: '0.015em',
        },
        body1: {
          fontSize: pxToRem(16),
          fontWeight: 'normal',
          fontStyle: 'normal',
          fontStretch: 'normal',
          lineHeight: '1.75',
          letterSpacing: '0.5px',
          color: 'rgba(0, 0, 0, 0.87)',
        },
        body2: {
          fontSize: pxToRem(14),
          fontWeight: 'normal',
          fontStyle: 'normal',
          fontStretch: 'normal',
          lineHeight: '1.43',
          letterSpacing: '0.3px',
          color: 'rgba(0, 0, 0, 0.87)',
        },
        caption: {
          fontSize: pxToRem(12),
          fontWeight: 'normal',
          fontStyle: 'normal',
          fontStretch: 'normal',
          lineHeight: '1.33',
          letterSpacing: '0.4px',
          color: 'rgba(0, 0, 0, 0.6)',
        },
        subtitle1: {
          fontSize: pxToRem(16),
          fontWeight: 'normal',
          fontStyle: 'normal',
          fontStretch: 'normal',
          lineHeight: '1.5',
          letterSpacing: '0.2px',
          color: 'rgba(0, 0, 0, 0.87)',
        },
        subtitle2: {
          fontSize: pxToRem(14),
          fontWeight: 500,
          fontStyle: 'normal',
          fontStretch: 'normal',
          lineHeight: '1.71',
          letterSpacing: '0.1px',
          color: 'rgba(0, 0, 0, 0.87)',
        },
      },
      MuiListItem: {
        // root: {
        //   '&$selected, &$selected:hover': {
        // backgroundColor: theme.palette.action.selected,
        // backgroundColor: 'rgba(63, 38, 178, 0.08)',
        // },
        // },
        button: {
          '&:hover': {
            backgroundColor: '#eaf2ff',
          },
        },
      },
      MuiModal: {
        root: {
          zIndex: 1350,
        },
      },
      MuiSwitch: {
        switchBase: {
          '&$checked': {
            color: '#25ca74',
          },
          '&$checked + $track': {
            backgroundColor: '#25ca74',
          },
          '&$checked&$disabled': {
            color: '#84e1b1', // < this is {color: '#25ca74', opacity: 0.5}
          },
        },
      },
      MuiTableCell: {
        root: {
          color: 'rgba(0, 0, 0, 0.87)',
          fontSize: pxToRem(13),
        },
        head: {
          color: 'rgba(0, 0, 0, 0.54)',
          fontSize: pxToRem(12),
          fontWeight: 'normal',
          letterSpacing: 'normal',
        },
      },
    },
    shape: {
      borderRadius: 4,
    },
    breakpoints,
  };

  return createMuiTheme(theme);
};
